import { ApolloError, ApolloQueryResult, OperationVariables, useQuery } from "@apollo/client";
import { useState } from "react";
import { getIssuesOrderBy } from "./helpers";
import { IIssue, IIssuesTableOperationFilters } from "./IIssueEntityInterfaces";
import { getIssues, getIssuesCount } from "api/queries/RCA/IssueAPI";

/** Where clause shape for issue list/count queries (Hasura-style filter object) */
export type IssueListWhere = Record<string, unknown>;

export interface UseIssueListReturn {
    items: IIssue[];
    totalCount: number;
    loading: boolean;
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<unknown>>;
}

/**
 * Shared hook for the count-then-list issue query pattern.
 * Used by GetIssues and GetUserIssues to avoid duplicating the two-query flow and state.
 */
export function useIssueList(
    whereVariables: IssueListWhere,
    tableOperationFilters: IIssuesTableOperationFilters,
): UseIssueListReturn {
    const [items, setItems] = useState<IIssue[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    useQuery(getIssuesCount, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            if (data?.issue_aggregate) {
                const count: number = data.issue_aggregate.aggregate?.count ?? 0;
                setTotalCount(count);
                if (count < 1) {
                    setItems([]);
                }
            }
        },
        variables: {
            where: whereVariables,
        },
    });

    const { loading, error, refetch } = useQuery(getIssues, {
        fetchPolicy: "no-cache",
        skip: !totalCount,
        onCompleted: (data) => {
            if (data?.issue && Array.isArray(data.issue)) {
                setItems(data.issue);
            }
        },
        variables: {
            limit: tableOperationFilters.limit,
            offset: tableOperationFilters.offset,
            order_by: getIssuesOrderBy(tableOperationFilters),
            where: whereVariables,
        },
    });

    return {
        items,
        totalCount,
        loading,
        apolloQueryError: error,
        refetch,
    };
}
